import { defineComponent, h, ref, watch, onMounted, computed } from 'vue';
import { TagField } from '@/ui/components/form';
import { useTask } from '@/ui/concerns';
import { list as getChecklistList } from '@/services/api/resources/checklist';
import { SortDirection } from '@/ui/types';
import { debounce } from 'lodash';
import { ChecklistList, ChecklistRecord } from '@/entities/checklist';
import { emptyList } from '@/services/api';
import { t } from '@/ui/i18n/i18n';

interface ChecklistAutoCompleteProps {
  value: number[];
  disabled: boolean;
  isStackable?: boolean;
}

export const ChecklistAutoComplete = defineComponent<ChecklistAutoCompleteProps>({
  name: 'ChecklistAutoComplete',
  props: ['value', 'disabled', 'isStackable'],
  setup(props, ctx) {
    const search = ref('');

    const selectedItems = ref<ChecklistRecord[]>([]);

    const checklists = ref<ChecklistList>(emptyList());
    const getList = useTask(
      () => getChecklistList({
        page: 1,
        perPage: 20,
        search: search.value,
        category: [],
        sort: 'createdAt',
        dir: SortDirection.Descending,
        status: null,
        entityItem: [],
        conductibleBy: [],
      }),
      listResponse => {
        const list = [...checklists.value.data, ...listResponse.data];

        checklists.value.data = list.filter(c => !checklists.value.data.some(ch => ch.id === c.id));
      },
    );

    const debouncedGetList = debounce(() => getList.run(), 1000);

    const mapOptions = (checklist: ChecklistRecord) => ({
      label: checklist.data.title,
      id: checklist.id,
      value: checklist.id,
    });

    const options = computed(() => {
      if (props.isStackable) {
        return checklists.value.data.map(mapOptions);
      }
      return [
        ...selectedItems.value.map(mapOptions),
        ...checklists.value.data.map(mapOptions),
      ];
    });

    watch(search, debouncedGetList);
    onMounted(getList.run);

    return function render() {
      return h(TagField, {
        props: {
          name: 'checklist',
          label: t('common.template'),
          value: props.value,
          required: false,
          placeholder: t('common.template'),
          disabled: false,
          options: options.value,
          errors: [],
          hideLabel: true,
          isStackable: props.isStackable,
        },
        on: {
          input: (e: number[]) => {
            ctx.emit('input', e);

            e.forEach(id => {
              const optionSelected = checklists.value.data.find(checklist => checklist.id === id);

              if (optionSelected) {
                selectedItems.value.push(optionSelected);
              }
            });
          },
          typing: (e: string) => {
            search.value = e;
          },
        },
      });
    };
  },
});

export function checklistAutoComplete<T>(
  props: ChecklistAutoCompleteProps & { classes?: string },
  on: Record<string, Function>,
) {
  return h(ChecklistAutoComplete, { class: props.classes, props, on });
}
